import {
	getCloudId,
	getHelpCenterAri,
	getWorkspaceAri,
	getLayoutAri,
	getMeta,
} from '@helpcenter/meta';
import { getRelayEnvironment } from '@atlassian/help-center-common-util/relay';
import type { Environment } from 'react-relay';
import type { ResourceStoreContext } from 'react-resource-router/resources';
import { createResourcesPlugin } from 'react-resource-router/resources';
import { PROJECT_NAME } from 'routes/common/constants';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import {
	getIsHelpHub,
	isCSMHelpCenter,
} from '@atlassian/help-center-common-util/advanced-help-center';
import { getEnv } from '@atlassian/help-center-common-util/env';

const shouldAddEdgeFallbackForHelpCenterType = (helpCenterType: string): boolean =>
	isCSMHelpCenter(helpCenterType) || helpCenterType === 'HELP_HUB';

const customContext: ResourceStoreContext & { relayEnvironment?: Environment } = {
	cloudId: getCloudId(),
	productName: PROJECT_NAME,
	helpCenterAri: getHelpCenterAri(),
	workspaceAri: getWorkspaceAri(),
	layoutAri: getLayoutAri(),
	isUserLoggedIn: Boolean(getMeta('ajs-atlassian-account-id')),
	accountType: getMeta('ajs-account-type') || '',
	relayEnvironment: getRelayEnvironment(),
	// Read by the shared hub page resource, which gates a query variable on it and so needs it
	// before the query runs. Just the surface type, not ANDed with `enable_rovo_in_help_hubs`:
	// evaluating a feature gate at module scope resolves to false under SSR
	// (@atlaskit/platform/no-module-level-eval). The resource ANDs its own gate at call time.
	isHelpHub: getIsHelpHub(),
	// Signal the shared hub bootstrap resource to add the edge-fallback request
	// header (`X-Edge-Fallback-To-Unidentified`) for CSM Support Site and JSM Help
	// Hub consumers. Gated so that when `csm_stargate_unidentified_fallback_on_bad_cookie`
	// is off this stays `undefined` and no behaviour changes for any consumer.
	shouldAddEdgeFallbackToUnidentified: fg('csm_stargate_unidentified_fallback_on_bad_cookie')
		? shouldAddEdgeFallbackForHelpCenterType(getEnv().helpCenterType)
		: undefined,
};

/**
 * WARNING: This plugin always needs intialization at module level.
 * Calling it at runtime will make SSR fail as the `customContext` wouldn't be available.
 */

type ResourcesPlugin = ReturnType<typeof createResourcesPlugin>;

export const resourcesPlugin: ResourcesPlugin = createResourcesPlugin({
	context: customContext,
});
